import { boxEingerichtet } from '../installation';
import { useEffect, useState, type ReactElement } from 'react';
import { api, type AblageBefund, type NextcloudSettingsView } from '../api';
import { Sheet } from './Sheet';
import { useNachgeliefert } from '../nachgeliefert';

/**
 * Die Ablage: wo das Material wirklich liegt.
 *
 * Dieselben vier Werte, die auch in der Umgebung stehen können — nur eingebbar,
 * ohne den Stapel neu zu starten. Das Passwort wird verschlüsselt gespeichert
 * und nie zurückgeschickt; ein leeres Feld heißt „lass es, wie es ist“.
 *
 * Der Knopf „Verbindung prüfen“ steht bewusst gleich daneben und nicht am Ende
 * eines Handbuchs. Eine Adresse einzutippen und erst beim nächsten Export zu
 * merken, dass sie falsch war, ist die schlechtere Reihenfolge — und er geht
 * den *ganzen* Weg: lesen, eine winzige Probe schreiben, zurücklesen,
 * vergleichen, aufräumen. Ein Zugang, der nur lesen darf, sieht beim Auflisten
 * völlig gesund aus.
 */
export function AblageSettingsSheet({ onClose, onBox }: { onClose: () => void; onBox: () => void }): ReactElement {
  const [ansicht, setAnsicht] = useState<NextcloudSettingsView | null>(null);
  const [formular, setFormular, formularNachliefern] = useNachgeliefert({ url: '', benutzer: '', passwort: '', wurzel: '' });
  const [laeuft, setLaeuft] = useState(false);
  const [befund, setBefund] = useState<AblageBefund | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    try {
      const { nextcloud } = await api.nextcloudSettings();
      setAnsicht(nextcloud);
      formularNachliefern((f) => ({ ...f, url: nextcloud.url, benutzer: nextcloud.benutzer, wurzel: nextcloud.wurzel }));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die Einstellungen ließen sich nicht laden.');
    }
  };
  useEffect(() => {
    void laden();
  }, []);

  const tue = async (was: () => Promise<unknown>) => {
    setLaeuft(true);
    setFehler(null);
    try {
      await was();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setLaeuft(false);
    }
  };

  const [blaettern, setBlaettern] = useState<{ pfad: string; ordner: { id: string; name: string }[] } | null>(null);
  const speichere = async () => {
    const { nextcloud } = await api.saveNextcloudSettings(formular);
    setAnsicht(nextcloud);
    setFormular((f) => ({ ...f, passwort: '' }));
  };
  const oeffne = async (pfad: string) => {
    const stand = await api.ablageOrdner(pfad);
    setBlaettern({ pfad: stand.id, ordner: stand.ordner });
  };

  const ungespeichert =
    Boolean(formular.passwort) ||
    formular.url.trim() !== (ansicht?.url ?? '').trim() ||
    formular.benutzer.trim() !== (ansicht?.benutzer ?? '').trim() ||
    formular.wurzel.trim() !== (ansicht?.wurzel ?? '').trim();

  const herkunft =
    ansicht?.quelle === 'datenbank'
      ? 'Hier eingetragen'
      : ansicht?.quelle === 'umgebung'
        ? 'Aus der Umgebung'
        : 'Noch nichts eingetragen';

  return (
    <Sheet title="Ablage (Nextcloud)" onClose={onClose}>
      {fehler ? <p className="error-text">{fehler}</p> : null}

      <div className="card" style={{ margin: '0 0 14px' }}>
        <div className="title" data-herkunft={ansicht?.quelle ?? '-'}>{herkunft}</div>
        <p className="hint" style={{ marginTop: 4 }}>
          {ansicht?.quelle === 'keine'
            ? boxEingerichtet()
              ? 'Ohne Nextcloud bleibt es bei Box.'
              : 'Ohne Nextcloud gibt es keine Ablage: Material wird dann nur im Projekt hochgeladen.'
            : ansicht?.quelle === 'umgebung'
              ? 'Was du hier einträgst, hat Vorrang vor der Umgebung.'
              : 'Diese Angaben gelten. „Vergessen“ nimmt wieder die Umgebung.'}
        </p>
        {ansicht?.unlesbar ? (
          <p className="hint" style={{ color: 'var(--warn-text)' }}>
            Das gespeicherte Passwort lässt sich nicht mehr lesen — das passiert, wenn <code>SESSION_SECRET</code> geändert
            wurde. Einmal neu eintragen genügt.
          </p>
        ) : null}
      </div>

      <label className="field">
        <span>Adresse des DAV-Zugangs</span>
        <input
          value={formular.url}
          onChange={(e) => setFormular({ ...formular, url: e.target.value })}
          placeholder="https://wolke.example.de/remote.php/dav/files/DEINKONTO/"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      <p className="hint" style={{ marginTop: -6 }}>
        Mit dem Konto am Ende. In Nextcloud steht sie unter <i>Einstellungen → Verwaltung → WebDAV</i>.
      </p>

      <label className="field">
        <span>Konto</span>
        <input
          value={formular.benutzer}
          onChange={(e) => setFormular({ ...formular, benutzer: e.target.value })}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>

      <label className="field">
        <span>App-Passwort{ansicht?.hatPasswort ? ' (gespeichert)' : ''}</span>
        <input
          type="password"
          value={formular.passwort}
          onChange={(e) => setFormular({ ...formular, passwort: e.target.value })}
          placeholder={ansicht?.hatPasswort ? 'Leer lassen heißt: unverändert' : ''}
          autoComplete="new-password"
        />
      </label>
      <p className="hint" style={{ marginTop: -6 }}>
        Ein <b>App-Passwort</b> aus <i>Einstellungen → Sicherheit</i>, nicht dein Kontopasswort — es lässt sich einzeln
        widerrufen, ohne dass sich sonst etwas ändert.
      </p>

      <label className="field">
        <span>Ordner für neue Projekte</span>
        <input
          value={formular.wurzel}
          onChange={(e) => setFormular({ ...formular, wurzel: e.target.value })}
          placeholder="leer = ganz oben"
          data-wurzel-feld
        />
      </label>
      {/* **Aussuchen statt tippen** (Sascha, 3. Oktober: „warum kann ich hier
          keinen ordner wählen?“). Dasselbe Blättern wie am Projekt (7.50),
          nur dass hier der Ordner gewählt wird, unter dem neue Projekte
          liegen. Geblättert wird in der Ablage, die gilt -- was im Formular
          steht, wird dafür erst gespeichert. */}
      {blaettern ? (
        <div className="list" style={{ marginBottom: 12 }} data-wurzel-blaettern={blaettern.pfad}>
          <div className="row" style={{ textAlign: 'left', display: 'block' }}>
            <strong style={{ display: 'block' }}>/{blaettern.pfad}</strong>
          </div>
          {blaettern.pfad ? (
            <button
              className="row"
              style={{ textAlign: 'left' }}
              disabled={laeuft}
              data-wurzel-hoch
              onClick={() => void tue(() => oeffne(blaettern.pfad.includes('/') ? blaettern.pfad.replace(/\/[^/]+$/, '') : ''))}
            >
              ‹ Eine Ebene höher
            </button>
          ) : null}
          {blaettern.ordner.map((o) => (
            <button
              key={o.id}
              className="row"
              style={{ textAlign: 'left' }}
              disabled={laeuft}
              data-wurzel-ordner={o.id}
              onClick={() => void tue(() => oeffne(o.id))}
            >
              {o.name}
            </button>
          ))}
          {blaettern.ordner.length === 0 ? <p className="hint">Hier liegen keine Unterordner.</p> : null}
          <button
            className="primary"
            style={{ marginTop: 8 }}
            disabled={laeuft}
            data-wurzel-nehmen
            onClick={() =>
              void tue(async () => {
                // Erst ins Feld, dann speichern: was man gewählt hat, steht
                // sofort da und nicht erst, wenn der Server geantwortet hat.
                const neu = { ...formular, passwort: '', wurzel: blaettern.pfad };
                setFormular(neu);
                setBlaettern(null);
                const { nextcloud } = await api.saveNextcloudSettings(neu);
                setAnsicht(nextcloud);
              })
            }
          >
            {blaettern.pfad ? `„${blaettern.pfad.split('/').pop()}“ nehmen` : 'Ganz oben nehmen'}
          </button>
        </div>
      ) : (
        <button
          className="row"
          style={{ textAlign: 'left', marginTop: -6, marginBottom: 12 }}
          disabled={laeuft || !formular.url.trim() || !formular.benutzer.trim()}
          data-wurzel-waehlen
          onClick={() =>
            void tue(async () => {
              if (ungespeichert) await speichere();
              await oeffne('');
            })
          }
        >
          Ordner aussuchen…
        </button>
      )}

      {befund ? (
        <div className="card" style={{ margin: '0 0 12px' }} data-befund={befund.ok ? 'ok' : befund.schritt}>
          <div className="title" style={{ color: befund.ok ? 'var(--ok-text)' : 'var(--err-text)' }}>
            {befund.ok ? 'Die Ablage antwortet' : 'Da klemmt etwas'}
          </div>
          <p style={{ margin: '6px 0 0' }}>{befund.satz}</p>
          <p className="hint" style={{ marginTop: 6 }}>
            {befund.anbieter} · Ordner {befund.wurzel}
          </p>
        </div>
      ) : null}

      <div className="stack">
        <button
          className="primary"
          disabled={laeuft || !formular.url.trim() || !formular.benutzer.trim()}
          onClick={() =>
            void tue(async () => {
              const { nextcloud } = await api.saveNextcloudSettings(formular);
              setAnsicht(nextcloud);
              setFormular((f) => ({ ...f, passwort: '' }));
              setBefund(null);
            })
          }
        >
          Speichern
        </button>
        <button
          disabled={laeuft}
          onClick={() =>
            void tue(async () => {
              setBefund(null);
              // Geprüft wird, was gilt -- also erst speichern, was im Formular
              // steht. Sonst prüfte der Knopf die alte Ablage, und nach dem
              // Eintippen stand dort noch „Box“ (Sascha, 3. Oktober).
              if (ungespeichert && formular.url.trim() && formular.benutzer.trim()) await speichere();
              const { befund: b } = await api.pruefeAblage();
              setBefund(b);
            })
          }
          data-pruefen
        >
          {laeuft ? 'Einen Moment…' : 'Verbindung prüfen'}
        </button>
        {ansicht?.quelle === 'datenbank' ? (
          <button
            className="ghost"
            disabled={laeuft}
            onClick={() =>
              void tue(async () => {
                const { nextcloud } = await api.clearNextcloudSettings();
                setAnsicht(nextcloud);
                setFormular({ url: nextcloud.url, benutzer: nextcloud.benutzer, passwort: '', wurzel: nextcloud.wurzel });
                setBefund(null);
              })
            }
          >
            Vergessen
          </button>
        ) : null}
      </div>
      <p className="hint">
        Geprüft wird der ganze Weg: lesen, eine winzige Probe schreiben, zurücklesen, vergleichen, wieder wegräumen. Am
        Material wird nichts angefasst.
      </p>

      {/* Box steht hier und nicht mehr im Menü daneben: Nextcloud ist der
          Hauptweg, Box das, was weiterläuft, solange Projekte darauf liegen
          (7.23). Zwei gleichrangige Einträge sagten das Gegenteil. */}
      {/* Und nur, wo Box eingerichtet ist (7.159): an einer Installation
          ohne Box ist es kein alter Weg, sondern gar keiner. */}
      {boxEingerichtet() ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '20px 0 8px' }}>Der alte Weg</h3>
          <button className="row" style={{ textAlign: 'left', display: 'block' }} onClick={onBox} data-box-oeffnen>
            <strong style={{ display: 'block' }}>Box</strong>
            <span className="hint" style={{ margin: 0 }}>
              Zugangsdaten für Box — für Projekte, die noch dort liegen. Neue Projekte gehören in die Ablage.
            </span>
          </button>
        </>
      ) : null}
    </Sheet>
  );
}
